import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

// Manager overview of production checkpoints: who confirmed how many spools, when, and
// whether the answer came late (lower reliability).

interface CheckpointRow {
  id: number;
  kind: 'periodic' | 'daily' | 'final';
  status: 'pending' | 'answered' | 'skipped';
  line_id: number;
  line_name: string | null;
  requested_at: string;
  answered_at: string | null;
  user_name: string | null;
  declared_quantity: number | null;
  delta_quantity: number | null;
  is_late: boolean;
  late_seconds: number | null;
  shift: string | null;
  item: { id: number; material: string | null; kolor: string | null; indeks_kat: string | null; nazwa_oryg: string | null; do_produkcji: number } | null;
  notes: string | null;
}

interface Summary { total: number; answered: number; pending: number; skipped: number; late: number; delta_total: number }

interface LineOption { id: number; name: string }

interface CheckpointSettings { enabled: boolean; active_since: string | null }

const fmtTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtLate = (seconds: number | null): string => {
  if (!seconds) return '';
  const m = Math.round(seconds / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}min` : `${m}min`;
};

const AdminCheckpoints: React.FC = () => {
  const { t } = useLanguage();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lineId, setLineId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [lines, setLines] = useState<LineOption[]>([]);
  const [rows, setRows] = useState<CheckpointRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [lateMinutes, setLateMinutes] = useState(15);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<CheckpointSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    api.get('/production-lines').then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data.lines ?? res.data.data ?? []);
      setLines(list.map((l: any) => ({ id: l.id, name: l.name })));
    }).catch(() => {});
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/checkpoints', { params: { date, line_id: lineId || undefined, status: status || undefined } });
      setRows(res.data.checkpoints);
      setSummary(res.data.summary);
      setLateMinutes(res.data.late_minutes);
      setSettings(res.data.settings ?? null);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [date, lineId, status]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Włączenie = checkpointy wymagane tylko dla pozycji rozpoczętych od tej chwili;
  // wyłączenie zamyka oczekujące, więc nic nie blokuje linii.
  const toggleCheckpoints = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const res = await api.put('/admin/checkpoints/settings', { enabled: !settings.enabled });
      setSettings(res.data.settings);
      fetchRows();
    } catch (err: any) {
      alert(err?.response?.data?.message ?? t('checkpointsAdmin.toggleError'));
    } finally {
      setSavingSettings(false);
    }
  };

  const statusBadge = (row: CheckpointRow) => {
    if (row.status === 'pending') return <span style={{ ...badge, background: '#fff3cd', color: '#92400e' }}>⏳ {t('checkpointsAdmin.statusPending')}</span>;
    if (row.status === 'skipped') return <span style={{ ...badge, background: '#e5e7eb', color: '#475569' }}>{t('checkpointsAdmin.statusSkipped')}</span>;
    if (row.is_late) return <span style={{ ...badge, background: '#fee2e2', color: '#991b1b' }}>⚠ {t('checkpointsAdmin.statusLate')} (+{fmtLate(row.late_seconds)})</span>;
    return <span style={{ ...badge, background: '#d1fae5', color: '#065f46' }}>✔ {t('checkpointsAdmin.statusOk')}</span>;
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>⏱️ {t('checkpointsAdmin.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('checkpointsAdmin.subtitle', { minutes: lateMinutes })}</p>
      </div>

      {/* On/off switch */}
      {settings && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 16, background: settings.enabled ? '#ecfdf5' : '#f8fafc', border: `1px solid ${settings.enabled ? '#a7f3d0' : '#e2e8f0'}`, borderRadius: 10, padding: '12px 16px' }}>
          <div>
            <div style={{ fontWeight: 700, color: settings.enabled ? '#065f46' : '#475569' }}>
              {settings.enabled
                ? t('checkpointsAdmin.enabledSince', { since: fmtTime(settings.active_since) })
                : t('checkpointsAdmin.disabled')}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('checkpointsAdmin.switchHint')}</div>
          </div>
          <button
            onClick={toggleCheckpoints}
            disabled={savingSettings}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13, cursor: savingSettings ? 'wait' : 'pointer', color: '#fff', background: settings.enabled ? '#dc3545' : '#667eea' }}
          >
            {settings.enabled ? t('checkpointsAdmin.disable') : t('checkpointsAdmin.enable')}
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px' }}>
        <label style={lbl}>{t('checkpointsAdmin.date')}
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
        </label>
        <label style={lbl}>{t('checkpointsAdmin.line')}
          <select value={lineId} onChange={e => setLineId(e.target.value)} style={inp}>
            <option value="">{t('checkpointsAdmin.allLines')}</option>
            {lines.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <label style={lbl}>{t('checkpointsAdmin.status')}
          <select value={status} onChange={e => setStatus(e.target.value)} style={inp}>
            <option value="">{t('checkpointsAdmin.allStatuses')}</option>
            <option value="answered">{t('checkpointsAdmin.statusAnswered')}</option>
            <option value="pending">{t('checkpointsAdmin.statusPending')}</option>
            <option value="skipped">{t('checkpointsAdmin.statusSkipped')}</option>
          </select>
        </label>
      </div>

      {/* Summary */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: t('checkpointsAdmin.sumTotal'), value: summary.total, color: '#667eea', bg: '#eef2ff' },
            { label: t('checkpointsAdmin.sumAnswered'), value: summary.answered, color: '#10b981', bg: '#ecfdf5' },
            { label: t('checkpointsAdmin.sumPending'), value: summary.pending, color: '#d97706', bg: '#fffbeb' },
            { label: t('checkpointsAdmin.sumLate'), value: summary.late, color: '#dc3545', bg: '#fef2f2' },
            { label: t('checkpointsAdmin.sumDelta'), value: summary.delta_total, color: '#2563eb', bg: '#eff6ff' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} style={{ flex: 1, minWidth: 130, background: bg, border: `1px solid ${color}30`, borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={th}>{t('checkpointsAdmin.colRequested')}</th>
              <th style={th}>{t('checkpointsAdmin.colKind')}</th>
              <th style={th}>{t('checkpointsAdmin.colLine')}</th>
              <th style={th}>{t('checkpointsAdmin.colProduct')}</th>
              <th style={th}>{t('checkpointsAdmin.colDeclared')}</th>
              <th style={th}>{t('checkpointsAdmin.colDelta')}</th>
              <th style={th}>{t('checkpointsAdmin.colAnswered')}</th>
              <th style={th}>{t('checkpointsAdmin.colOperator')}</th>
              <th style={th}>{t('checkpointsAdmin.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('common.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 32 }}>{t('checkpointsAdmin.empty')}</td></tr>
            ) : rows.map(row => {
              const colour = row.item?.kolor?.includes('\n') ? row.item.kolor.split('\n').join('/') : row.item?.kolor;
              return (
                <tr key={row.id} style={{ background: row.is_late ? '#fff7f7' : row.status === 'pending' ? '#fffdf5' : '#fff' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtTime(row.requested_at)}</td>
                  <td style={td}>{row.kind === 'daily' ? `🕛 ${t('checkpointsAdmin.kindDaily')}` : `⏱ ${t('checkpointsAdmin.kindPeriodic')}`}</td>
                  <td style={td}>{row.line_name ?? '—'}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{[row.item?.material, colour].filter(Boolean).join(' ') || '—'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{row.item?.indeks_kat} · {t('checkpoint.plan')} {row.item?.do_produkcji}</div>
                  </td>
                  <td style={{ ...td, fontWeight: 700, textAlign: 'center' }}>{row.declared_quantity ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: (row.delta_quantity ?? 0) < 0 ? '#dc3545' : '#059669' }}>
                    {row.delta_quantity === null ? '—' : (row.delta_quantity > 0 ? `+${row.delta_quantity}` : row.delta_quantity)}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtTime(row.answered_at)}</td>
                  <td style={td}>{row.user_name ?? '—'}</td>
                  <td style={td}>{statusBadge(row)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' };
const inp: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 150 };
const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' };
const badge: React.CSSProperties = { display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' };

export default AdminCheckpoints;
